import React, { useEffect, useState, useCallback } from 'react';
import {
  fetchTeleobiConfig, saveTeleobiConfig, deleteTeleobiConfig, dispatchTeleobiSend,
} from '../services/api';

// ─── Types ──────────────────────────────────────────────────────────────

interface TeleobiConfig {
  id?: number;
  tenant_id: number;
  api_token: string;
  phone_number_id: string;
  template_id: string;
  template_name?: string | null;
  template_language?: string | null;
  template_header_media_url?: string | null;
  template_variables: string[];
  enabled?: boolean;
  retry_after_hours?: number;
}

// ─── Styling ─────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
  padding: 20, marginBottom: 16,
};
const label: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 4,
};
const input: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border)',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
};

// ─── Page ────────────────────────────────────────────────────────────────

const TeleobiConfigPage: React.FC = () => {
  const [tenantId, setTenantId] = useState<string>('10');
  const [cfg, setCfg]           = useState<TeleobiConfig | null>(null);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState<string | null>(null);
  const [flash, setFlash]       = useState<string | null>(null);

  const [dispatchLeadId, setDispatchLeadId] = useState<string>('');
  const [dispatchRunning, setDispatchRunning] = useState(false);
  const [dispatchResult, setDispatchResult]   = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true); setErr(null); setFlash(null);
    try {
      const c = await fetchTeleobiConfig(Number(tenantId));
      // Backend returns a bare {tenant_id, enabled:false, ...} when no row exists.
      // Coerce template_variables to an array of exactly 4 slots for the UI.
      const vars = Array.isArray(c.template_variables) ? c.template_variables : [];
      const padded = [...vars, '', '', '', ''].slice(0, 4);
      setCfg({
        id: c.id,
        tenant_id: Number(tenantId),
        api_token: c.api_token ?? '',
        phone_number_id: c.phone_number_id ?? '',
        template_id: c.template_id ?? '',
        template_name: c.template_name ?? '',
        template_language: c.template_language ?? '',
        template_header_media_url: c.template_header_media_url ?? '',
        template_variables: padded,
        enabled: !!c.enabled,
        retry_after_hours: c.retry_after_hours ?? 24,
      });
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load config');
    } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const setField = <K extends keyof TeleobiConfig>(k: K, v: TeleobiConfig[K]) => {
    setCfg(prev => prev ? { ...prev, [k]: v } : prev);
  };

  const setVar = (idx: number, v: string) => {
    setCfg(prev => {
      if (!prev) return prev;
      const vars = [...prev.template_variables];
      vars[idx] = v;
      return { ...prev, template_variables: vars };
    });
  };

  const save = async () => {
    if (!cfg) return;
    setSaving(true); setFlash(null); setErr(null);
    try {
      const saved = await saveTeleobiConfig({
        ...cfg,
        template_variables: cfg.template_variables.filter(v => v && v.trim().length > 0),
      });
      setFlash(`Saved. enabled=${saved.enabled ? 'true' : 'false'}.`);
      load();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? JSON.stringify(e?.response?.data ?? {}) ?? 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!cfg?.id) return;
    if (!window.confirm(`Delete Teleobi config for tenant ${cfg.tenant_id}? Outbound WhatsApp will stop.`)) return;
    setSaving(true);
    try {
      await deleteTeleobiConfig(cfg.tenant_id);
      setFlash('Deleted.');
      load();
    } catch (e: any) {
      setErr('Delete failed: ' + (e?.response?.data?.error ?? e?.message));
    } finally { setSaving(false); }
  };

  const runDispatch = async () => {
    if (!dispatchLeadId) return;
    setDispatchRunning(true); setDispatchResult(null);
    try {
      const r = await dispatchTeleobiSend(Number(dispatchLeadId));
      setDispatchResult(`Queued for lead ${r.lead_id} (tenant ${r.tenant_id}). Job will no-op if disabled or in cooldown.`);
    } catch (e: any) {
      setDispatchResult('Error: ' + (e?.response?.data?.error ?? e?.message));
    } finally { setDispatchRunning(false); }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 24px' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Teleobi WhatsApp Config</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 20, fontSize: 14 }}>
        Per-tenant Teleobi template send. On lead-create, if <b>enabled=true</b>, the CRM queues a
        job that posts to <code>dash.teleobi.com/api/v1/whatsapp/send/template</code> with these values.
        Credentials are stored in plaintext (owner preference); rotate the api_token here whenever
        Teleobi issues a new one.
      </p>

      {/* Tenant selector */}
      <div style={card}>
        <label style={label}>Tenant ID</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="number" value={tenantId} onChange={e => setTenantId(e.target.value)}
            style={{ ...input, width: 140 }}
          />
          <button className="btn" onClick={load} disabled={loading} style={{ padding: '8px 14px', fontSize: 13 }}>
            {loading ? 'Loading…' : 'Load'}
          </button>
        </div>
      </div>

      {err   && <div style={{ ...card, color: '#dc2626', fontSize: 13 }}>{err}</div>}
      {flash && <div style={{ ...card, color: '#059669', fontSize: 13 }}>{flash}</div>}

      {cfg && (
        <>
          {/* Credentials */}
          <div style={card}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Credentials + template</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={label}>API token</label>
                <input type="text" value={cfg.api_token}
                  onChange={e => setField('api_token', e.target.value)}
                  style={input} placeholder="14776|…"
                />
              </div>
              <div>
                <label style={label}>Phone Number ID</label>
                <input type="text" value={cfg.phone_number_id}
                  onChange={e => setField('phone_number_id', e.target.value)}
                  style={input} placeholder="761956213671965"
                />
              </div>
              <div>
                <label style={label}>Template ID</label>
                <input type="text" value={cfg.template_id}
                  onChange={e => setField('template_id', e.target.value)}
                  style={input} placeholder="362474"
                />
              </div>
              <div>
                <label style={label}>Template name (label only)</label>
                <input type="text" value={cfg.template_name ?? ''}
                  onChange={e => setField('template_name', e.target.value)}
                  style={input} placeholder="lyk_26_Icustomer"
                />
              </div>
              <div>
                <label style={label}>Template language (metadata)</label>
                <input type="text" value={cfg.template_language ?? ''}
                  onChange={e => setField('template_language', e.target.value)}
                  style={input} placeholder="en / hi"
                />
              </div>
              <div>
                <label style={label}>Retry after (hours)</label>
                <input type="number" value={cfg.retry_after_hours ?? 24}
                  onChange={e => setField('retry_after_hours', Number(e.target.value))}
                  style={input}
                />
              </div>
            </div>
            <div style={{ marginBottom: 4 }}>
              <label style={label}>Header media URL</label>
              <input type="text" value={cfg.template_header_media_url ?? ''}
                onChange={e => setField('template_header_media_url', e.target.value)}
                style={input}
                placeholder="https://bot-data.s3.ap-southeast-1.wasabisys.com/..."
              />
            </div>
          </div>

          {/* Template variables */}
          <div style={card}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Template variables (4 slots)</h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Sent as <code>templateVariable-one-1</code> … <code>templateVariable-four-4</code>. Leave a slot blank to omit.
            </p>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ marginBottom: 10 }}>
                <label style={label}>Variable {i + 1}</label>
                <textarea
                  value={cfg.template_variables[i] ?? ''}
                  onChange={e => setVar(i, e.target.value)}
                  style={{ ...input, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            ))}
          </div>

          {/* Enable + save */}
          <div style={card}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={!!cfg.enabled}
                onChange={e => setField('enabled', e.target.checked)}
              />
              <span><b>Enabled</b> — when off, the CRM queues jobs but they no-op immediately.</span>
            </label>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn" onClick={save} disabled={saving}
                style={{ padding: '8px 16px', fontSize: 13, background: '#059669', color: 'white', border: 'none', borderRadius: 6 }}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {cfg.id && (
                <button className="btn" onClick={remove} disabled={saving}
                  style={{ padding: '8px 16px', fontSize: 13, background: 'transparent', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 6 }}
                >
                  Delete config
                </button>
              )}
            </div>
          </div>

          {/* Manual dispatch */}
          <div style={card}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Manual test send</h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Queues a real send for a specific lead. Same code path as lead-create. Honours cooldown
              (won't spam a lead sent recently).
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={label}>Lead ID</label>
                <input type="number" value={dispatchLeadId} onChange={e => setDispatchLeadId(e.target.value)}
                  placeholder="e.g. 1088" style={input}
                />
              </div>
              <button className="btn" onClick={runDispatch} disabled={dispatchRunning || !dispatchLeadId}
                style={{ padding: '8px 14px', fontSize: 13, background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6 }}
              >
                {dispatchRunning ? 'Queuing…' : 'Queue send'}
              </button>
            </div>
            {dispatchResult && (
              <div style={{ marginTop: 10, fontSize: 13, color: dispatchResult.startsWith('Error') ? '#dc2626' : '#059669' }}>
                {dispatchResult}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TeleobiConfigPage;
